/**
 * 非同期処理の二重実行を防ぐ汎用フック
 *
 * 通信が遅いときにボタンを連打しても、実行中の処理が終わるまで後続の呼び出しを捨てる。
 * `isSubmitting` は表示用（disabled / ラベル切り替え）で、実際のロックは ref で行う。
 * state は次のレンダリングまで反映されないため、同一 tick 内の連打は state では弾けない。
 *
 * @example
 * const { isSubmitting, run } = useSubmitLock()
 *
 * const handleSubmit = () =>
 *   run(async () => {
 *     await createSomething()
 *   })
 *
 * <Button onClick={handleSubmit} disabled={isSubmitting}>保存</Button>
 */

import { useCallback, useEffect, useRef, useState } from 'react'

/** ロック中で処理がスキップされたことを示す戻り値 */
export const SUBMIT_SKIPPED = Symbol('submit-skipped')

/** run() の戻り値を使う側で T に絞り込むための型ガード */
export const isSubmitSkipped = <T>(result: T | typeof SUBMIT_SKIPPED): result is typeof SUBMIT_SKIPPED =>
  result === SUBMIT_SKIPPED

type SubmitLock = {
  isSubmitting: boolean
  /** 実行中なら task を呼ばずに SUBMIT_SKIPPED を返す。task の例外はそのまま伝播する */
  run: <T>(task: () => Promise<T>) => Promise<T | typeof SUBMIT_SKIPPED>
}

export const useSubmitLock = (): SubmitLock => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const lockedRef = useRef(false)
  const mountedRef = useRef(true)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  const run = useCallback(async <T>(task: () => Promise<T>): Promise<T | typeof SUBMIT_SKIPPED> => {
    if (lockedRef.current) return SUBMIT_SKIPPED
    lockedRef.current = true
    setIsSubmitting(true)
    try {
      return await task()
    } finally {
      lockedRef.current = false
      // シートを閉じた直後など、完了時にすでにアンマウントしていることがある
      if (mountedRef.current) setIsSubmitting(false)
    }
  }, [])

  return { isSubmitting, run }
}
